import { useState } from "react";
import { InfinityMark } from "../components/ConnectMyHubLogo";
import { useAuth } from "../context/AuthContext";
import { Navigate, Link, useSearchParams } from "react-router-dom";
import { fmtErr } from "../lib/api";
import { Loader2, Briefcase, UserPlus, ArrowRight } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const [sp] = useSearchParams();
  const next = sp.get("next");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) {
    if (next && next.startsWith("/")) return <Navigate to={next} replace />;
    return <Navigate to={user.role === "candidate" ? "/portal" : "/dashboard"} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try { await login(email, password); }
    catch (e) { setErr(fmtErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex bg-slate-950">
      <div className="hidden lg:flex flex-col justify-between p-12 flex-1 bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30" style={{backgroundImage:"radial-gradient(circle at 20% 30%, #0284C7 0%, transparent 50%), radial-gradient(circle at 80% 70%, #0369A1 0%, transparent 50%)"}} />
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-slate-900 flex items-center justify-center"><InfinityMark size={30}/></div>
            <div>
              <div className="font-display font-bold text-white text-lg">Connect<span className="text-teal-300">My</span>Hub<span className="text-teal-300 text-xs font-semibold ml-0.5">™</span></div>
              <div className="text-[10px] text-slate-400 uppercase tracking-widest">by My Pipeline Solutions</div>
            </div>
          </div>
        </div>
        <div className="relative z-10 max-w-md">
          <div className="text-[11px] text-sky-400 font-semibold tracking-widest uppercase mb-3">Global Workforce Operating System</div>
          <h1 className="text-white font-display font-bold text-4xl leading-tight tracking-tight mb-4">
            Don't ask who to hire.<br/>Ask what work needs to get done.
          </h1>
          <p className="text-slate-400 text-sm leading-relaxed">
            ConnectMyHub™ connects people, skills, businesses, and opportunities — direct hire, managed workforce, and AI-augmented teams from My Pipeline Solutions.
          </p>
          <div className="mt-8 grid grid-cols-5 gap-2 text-[10px] uppercase tracking-widest font-semibold text-slate-500">
            <div className="text-center py-2 border border-slate-800 rounded">Direct</div>
            <div className="text-center py-2 border border-slate-800 rounded">Managed</div>
            <div className="text-center py-2 border border-slate-800 rounded">AI</div>
            <div className="text-center py-2 border border-slate-800 rounded">Hybrid</div>
            <div className="text-center py-2 border border-slate-800 rounded">Teams</div>
          </div>
        </div>
        <div className="relative z-10 text-[11px] text-slate-500">© 2026 My Pipeline Solutions. All rights reserved.</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 bg-white overflow-y-auto">
        <div className="w-full max-w-sm py-6">
          <div className="mb-6">
            <div className="lg:hidden flex items-center gap-2 mb-6">
              <div className="w-9 h-9 rounded-lg bg-slate-900 flex items-center justify-center"><InfinityMark size={24}/></div>
              <div className="font-display font-bold">ConnectMyHub™</div>
            </div>
            <h2 className="font-display font-bold text-2xl text-slate-900 tracking-tight">Sign in</h2>
            <p className="text-sm text-slate-500 mt-1">Welcome back to ConnectMyHub™.</p>
          </div>

          <form data-testid="login-form" onSubmit={onSubmit}>
            <label className="block text-xs font-semibold text-slate-700 mb-1.5">Email address</label>
            <input
              data-testid="login-email"
              type="email" value={email} onChange={(e)=>setEmail(e.target.value)}
              placeholder="you@company.com"
              className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm focus:border-sky-500 outline-none mb-4"
              required autoFocus autoComplete="email"
            />
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-xs font-semibold text-slate-700">Password</label>
              <Link to="/forgot-password" data-testid="forgot-password-link"
                    className="text-[11px] font-semibold text-sky-600 hover:text-sky-700">Forgot password?</Link>
            </div>
            <input
              data-testid="login-password"
              type="password" value={password} onChange={(e)=>setPassword(e.target.value)}
              placeholder="Your password"
              className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm focus:border-sky-500 outline-none mb-4"
              required autoComplete="current-password"
            />
            {err && <div data-testid="login-error" className="mb-4 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}
            <button
              data-testid="login-submit"
              disabled={busy}
              className="w-full h-10 rounded-md bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
            >
              {busy && <Loader2 size={14} className="animate-spin"/>} Sign in
            </button>
          </form>

          {/* NEW-USER REGISTRATION PATHS */}
          <div className="mt-8 pt-6 border-t border-slate-200" data-testid="new-user-block">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-3 text-center">New to ConnectMyHub?</div>
            <div className="grid grid-cols-1 gap-3">
              <Link to={next ? `/portal/signup?next=${encodeURIComponent(next)}` : "/portal/signup"} data-testid="signup-candidate-cta"
                    className="group flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-sky-500 hover:bg-sky-50/40 transition-colors">
                <div className="w-9 h-9 rounded-md bg-sky-50 border border-sky-200 flex items-center justify-center shrink-0">
                  <UserPlus size={16} className="text-sky-700"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-semibold text-sky-700">I'm looking for work</div>
                  <div className="text-sm font-semibold text-slate-900 leading-tight">Create free candidate account</div>
                  <div className="text-[11px] text-slate-500">Free to join, apply, be matched, interview & get hired.</div>
                </div>
                <ArrowRight size={14} className="text-slate-400 group-hover:text-sky-600 shrink-0"/>
              </Link>

              <Link to="/employer-portal/signup" data-testid="signup-employer-cta"
                    className="group flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-slate-900 hover:bg-slate-50 transition-colors">
                <div className="w-9 h-9 rounded-md bg-slate-100 border border-slate-300 flex items-center justify-center shrink-0">
                  <Briefcase size={16} className="text-slate-800"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-semibold text-slate-700">I'm hiring</div>
                  <div className="text-sm font-semibold text-slate-900 leading-tight">Create employer account</div>
                  <div className="text-[11px] text-slate-500">Free to create — purchase a membership when you're ready.</div>
                </div>
                <ArrowRight size={14} className="text-slate-400 group-hover:text-slate-900 shrink-0"/>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
